import { CameraControls, OrthographicCamera } from '@react-three/drei';
import { Canvas, RootState } from '@react-three/fiber';
import { useEffect, useRef, useState } from 'react';
import { Vector2 } from 'three';
import { useSnapshot } from 'valtio';

import { applyShowSkeleton, sceneDeclaresYUp } from '@cartwhl/3d/gltf/utils';
import Lights from '@cartwhl/3d/r3f/components/Lights';
import ManualRenderLoop from '@cartwhl/3d/r3f/components/ManualRenderLoop';
import useGLTFWithCredentials from '@cartwhl/3d/r3f/hooks/useGLTFWithCredentials';
import { StudioModalName } from '@cartwhl/common/stores/navigationStore';
import {
  TRACKED_FEATURES,
  trackFeatureAbandoned,
  trackFeatureCompleted,
  trackFeatureStarted,
  trackFeatureStepCompleted,
} from '@cartwhl/tracking/featureTracking';
import Checkbox from '@cartwhl/ui/atoms/Checkbox';
import Text from '@cartwhl/ui/base/Text';
import CircleArrow from '@cartwhl/ui/icons/CircleArrow';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

import {
  characterPickerStore,
  navigationStore,
  noticesStore,
  sceneSettingsStore,
  userStore,
} from 'stores';

import { AUTORIGGING_STEPS, MARKERS, MODAL_MAX_WIDTH } from '../constants';
import { usePrepareObjectForRaycasting } from '../hooks';
import { BodyMarker } from '../types';
import { fitObjectToView } from '../utils';
import { findMarkersFromCharacter, getApiPayload } from '../utils';
import CharacterVerificationStage from './CharacterVerificationStage';
import DebugCamera from './DebugCamera';
import { DebugMesh } from './DebugMesh';
import { DebugPanelStep0 } from './DebugPanelStep0';
import { DebugPanelStep1 } from './DebugPanelStep1';
import { DraggableMarker } from './DraggableMarker';
import LoadingCharacter from './LoadingCharacter';
import ModalLayout from './ModalLayout';
import OrientationButtons from './OrientationButtons';
import SetInitalCameraPosition from './SetInitialCameraPosition';
import { SlotBelowCanvas } from './SlotBelowCanvas';
import Steps from './Steps';
import VerificationCameraButtons, {
  VerificationCameraButtonsSlot,
} from './VerificationCameraButtons';
import VerticalDivider from './VerticalDivider';
import WaitingForServer from './WaitingForServer';
import ZoomScopeView from './ZoomScopeView';

export default function CharacterRiggingModal({
  close,
  payload,
}: {
  close: () => void;
  payload: {
    characterID: string;
    autoRiggingInProgress?: boolean;
    showStep?: 0 | 1 | 2;
  };
}) {
  const threeState = useRef<RootState>(undefined);
  const { isCartwheeler } = useSnapshot(userStore.state);

  const [step, setStep] = useState<0 | 1 | 2>(payload.showStep ?? 0);
  const [rotation, setRotation] = useState({ x: 0, y: 0, z: 0 });
  const { characters } = useSnapshot(characterPickerStore.state);
  const { characterID } = payload;
  const character = characters?.find((c) => c.jobID === characterID);
  const [debug, setDebug] = useState(false);
  const [shiftX, setShiftX] = useState(0);
  const [isSymmetry, setIsSymmetry] = useState(true);
  const [bodyMarkers, setBodyMarkers] = useState<BodyMarker[]>(MARKERS);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const meshData = useGLTFWithCredentials(character?.meshPath);

  useEffect(() => {
    // If the payload is changed while the modal is open, update the step
    // This is a little workaround to avoid having global state for the modal
    setStep(payload.showStep ?? 0);
  }, [payload.showStep]);

  // HANDLE CHARACTER CHANGE
  useEffect(() => {
    if (meshData?.scene) {
      applyShowSkeleton(meshData, false);
      setBodyMarkers(findMarkersFromCharacter(meshData.scene));
      console.log('--- Character Model Ready');
    }
  }, [meshData?.scene]);

  useEffect(() => {
    trackFeatureStarted(TRACKED_FEATURES.CHARACTER_UPLOAD_RIGGING, {
      character_id: characterID,
      total_steps: AUTORIGGING_STEPS.length,
    });
    trackFeatureStepCompleted(TRACKED_FEATURES.CHARACTER_UPLOAD, 'entered_rigging', {
      character_id: characterID,
    });
  }, []);

  usePrepareObjectForRaycasting(meshData?.scene);

  // #region HANDLERS
  const onCanvasLoaded = (s: RootState) => {
    console.log('--- CANVAS READY');
    threeState.current = s;
    // Characters seem to always load before this event triggers.
    // Should be the only time the camera needs to be set
    fitView();
  };

  const handleRotateX = () => {
    setRotation((prev) => ({ ...prev, x: prev.x + Math.PI / 2 }));
  };

  const handleRotateY = () => {
    setRotation((prev) => ({ ...prev, y: prev.y + Math.PI / 2 }));
  };

  const handleReset = () => {
    setRotation({ x: 0, y: 0, z: 0 });
  };

  const handleMarkerWorldPositionChange = (
    markerId: string,
    value: THREE.Vector3 | undefined,
  ) => {
    setBodyMarkers((prev) =>
      prev.map((item) =>
        item.markers.some((m) => m.id === markerId)
          ? {
              ...item,
              markers: item.markers.map((m) =>
                m.id === markerId
                  ? { ...m, worldPosition: value, isError: !value }
                  : m,
              ),
            }
          : item,
      ),
    );
  };

  const handleMarkerScreenPositionChange = (
    markerId: string,
    pos: THREE.Vector2,
    isDragging: boolean,
  ) => {
    const rtn = new Array(bodyMarkers.length);
    let idx: number;

    // eslint-disable-next-line prefer-const
    for (let [i, v] of bodyMarkers.entries()) {
      // If marker being dragged, update its position & its mirror's
      if (
        (idx = v.markers.findIndex((m) => m.id === markerId)) > -1 &&
        canvasRef.current
      ) {
        v = { ...v };
        v.markers[idx].isDragging = isDragging;

        if (isSymmetry && v.markers.length === 2) {
          // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
          const xMid = canvasRef.current.clientWidth * 0.5 + shiftX; // User selected center divider
          const canRect = canvasRef.current.getBoundingClientRect();
          v.markers[idx].screenPosition = pos;

          // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
          // On Pointer Up while over canvas, apply Left / Right constraint
          if (
            !isDragging &&
            pos.x >= 0 &&
            pos.x <= canRect.width &&
            pos.y >= 0 &&
            pos.y <= canRect.height
          ) {
            const xOffset = xMid - pos.x;
            const xSign = markerId.startsWith('left_') ? -1 : 1;

            if (Math.sign(xOffset) !== xSign) pos.x = xMid - -xOffset;
          }

          // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
          // Move Mirrored target
          const altIdx = (idx + 1) % 2;
          const altPos = pos.clone();
          altPos.x = xMid + (xMid - pos.x);
          v.markers[altIdx].screenPosition = altPos;
          v.markers[altIdx].isDragging = isDragging;
        } else {
          // No Mirroring
          v.markers[idx].screenPosition = pos;
        }
      }

      // Save marker
      rtn[i] = v;
    }

    setBodyMarkers(rtn);
  };

  const handleMarkerReset = () => {
    const canRect = canvasRef.current?.getBoundingClientRect();
    if (canRect) {
      const rtn: Array<BodyMarker> = [];
      let elm: HTMLElement | null;
      let rect: DOMRect;
      let bm: BodyMarker;

      // Loop over all default body markers
      for (const marker of MARKERS) {
        bm = { ...marker };

        // Loop each sub marker to set its position
        for (let i = 0; i < bm.markers.length; i++) {
          if ((elm = document.getElementById(bm.markers[i].id))) {
            rect = elm.getBoundingClientRect();

            bm.markers[i].screenPosition = new Vector2(
              rect.left + rect.width / 2 - canRect.left,
              rect.top - canRect.top + rect.height / 2,
            );
          }
        }

        rtn.push(bm);
      }

      setBodyMarkers(rtn);
    }
  };
  // #endregion

  // #region HELPERS
  const fitView = () => {
    if (meshData?.scene && threeState.current) {
      const s = threeState.current.get(); // Get Current State
      fitObjectToView(meshData.scene, s.camera, s.size, 1.2);
    }
  };
  // #endregion

  return (
    <Modal
      title="Character auto-rigging"
      open
      onOpenChange={close}
      maxWidth={MODAL_MAX_WIDTH}
      fullscreenOnMobile
      fixedHeightContent
      closeOnClickOutside={false}
    >
      <ModalLayout.Root>
        <Steps step={step} steps={AUTORIGGING_STEPS} />

        <ModalLayout.Stage>
          {!payload.autoRiggingInProgress && (
            <ModalLayout.Sidebar>
              <Text
                as="h2"
                variant="studio/body/medium/md"
                className="mb-2 leading-5"
              >
                {AUTORIGGING_STEPS[step].title}
              </Text>
              <Text
                as="p"
                variant="studio/body/md"
                className="text-gray-60 dark:text-gray-45 leading-5"
              >
                {AUTORIGGING_STEPS[step].description}
              </Text>

              <div className="mt-4 flex-1">
                {step === 1 && (
                  <div className="mb-4 flex h-full flex-col gap-2">
                    {bodyMarkers.map((item) => (
                      <div key={item.name} className="flex items-center gap-2">
                        <Text
                          as="p"
                          variant="studio/body/medium/md"
                          className="basis-[84px]"
                        >
                          {item.name}:
                        </Text>
                        <div className="flex gap-6">
                          {item.markers.map((marker) => (
                            <div
                              key={marker.id}
                              id={marker.id}
                              className="border-gray-45 h-[30px] w-[30px] rounded-full border-2 border-dotted"
                            />
                          ))}
                        </div>
                      </div>
                    ))}

                    <div
                      className="mt-auto flex items-baseline justify-between gap-2"
                      style={{ border: '0px solid red' }}
                    >
                      <Button
                        variant="secondary"
                        size="sm"
                        className="flex-none"
                        icon={<CircleArrow />}
                        onClick={handleMarkerReset}
                      >
                        Reset
                      </Button>

                      <Checkbox
                        id="chkSymmetry"
                        checked={isSymmetry}
                        label="Use symmetry"
                        variant="checkbox"
                        className="flex-2"
                        color="settings"
                        setChecked={setIsSymmetry}
                      />
                    </div>
                  </div>
                )}
              </div>
            </ModalLayout.Sidebar>
          )}

          <ModalLayout.Main>
            {!meshData && !payload.autoRiggingInProgress && (
              <LoadingCharacter />
            )}

            {meshData && !payload.autoRiggingInProgress && (
              <>
                <Canvas
                  className="rounded-[inherit]"
                  gl={{ stencil: true }}
                  ref={canvasRef}
                  onCreated={onCanvasLoaded}
                >
                  <Lights />
                  <OrthographicCamera makeDefault />
                  <ManualRenderLoop />
                  {step === 1 && <ZoomScopeView />}

                  <primitive
                    object={meshData.scene}
                    position={[0, 0, 0]}
                    rotation={
                      step === 2
                        ? // Y-up GLBs are already canonical; skip the legacy -90
                          // X correction so they don't preview rotated 90 deg.
                          sceneDeclaresYUp(meshData.scene)
                          ? [0, 0, 0]
                          : [-Math.PI / 2, 0, 0]
                        : [rotation.x, rotation.y, rotation.z]
                    }
                  />

                  {debug && (
                    <>
                      <DebugCamera />

                      <mesh position={[0, 0, 0]}>
                        <sphereGeometry args={[0.02]} />
                        <meshStandardMaterial color={'hotpink'} />
                      </mesh>

                      {bodyMarkers.map((item) => {
                        return item.markers.map((marker) => {
                          if (!marker.worldPosition) return null;
                          return (
                            <mesh
                              key={marker.id}
                              position={marker.worldPosition}
                            >
                              <sphereGeometry args={[0.02]} />
                              <meshStandardMaterial color={item.color} />
                            </mesh>
                          );
                        });
                      })}

                      <DebugMesh targetObject={meshData.scene} />
                    </>
                  )}

                  {step === 1 && (
                    <>
                      {bodyMarkers.map((item) => {
                        return item.markers.map((marker) => (
                          <DraggableMarker
                            key={marker.id}
                            id={marker.id}
                            name={marker.name}
                            targetObject={meshData.scene}
                            onWorldPositionChange={(value) =>
                              handleMarkerWorldPositionChange(marker.id, value)
                            }
                            onScreenPositionChange={(pos, isDragging) =>
                              handleMarkerScreenPositionChange(
                                marker.id,
                                pos,
                                isDragging,
                              )
                            }
                            defaultWorldPosition={marker.worldPosition}
                            screenPosition={marker.screenPosition}
                            isDragging={marker.isDragging}
                            isError={marker.isError}
                            color={item.color}
                            showRay={debug}
                          />
                        ));
                      })}
                    </>
                  )}

                  {step === 2 && (
                    <>
                      <CharacterVerificationStage
                        meshData={meshData}
                        getPrimitive={false}
                        autoCamera={false}
                      />
                      <CameraControls makeDefault />
                      <SetInitalCameraPosition />
                      <VerificationCameraButtons />
                    </>
                  )}
                </Canvas>

                {step === 0 && (
                  <>
                    <OrientationButtons
                      rotateX={handleRotateX}
                      rotateY={handleRotateY}
                      reset={handleReset}
                      x={shiftX}
                      setX={setShiftX}
                    />
                    {debug && (
                      <DebugPanelStep0
                        x={shiftX}
                        rotateX={rotation.x}
                        rotateY={rotation.y}
                      />
                    )}
                  </>
                )}

                {step === 1 && (
                  <>
                    <SlotBelowCanvas.Out />
                    <VerticalDivider x={shiftX} />
                  </>
                )}

                {step === 1 && debug && (
                  <DebugPanelStep1 bodyMarkers={bodyMarkers} />
                )}

                <VerificationCameraButtonsSlot.Out />

                {isCartwheeler && (
                  <div className="absolute top-0 right-0 z-0 opacity-0 hover:opacity-100">
                    <Checkbox
                      id="debug"
                      checked={debug}
                      setChecked={setDebug}
                      label="Debug"
                      variant="checkbox"
                      className="opacity-50"
                    />
                  </div>
                )}
              </>
            )}
          </ModalLayout.Main>

          {payload.autoRiggingInProgress && (
            <WaitingForServer text="Generating character rig..." />
          )}
        </ModalLayout.Stage>

        <ModalLayout.Buttons>
          <Button
            variant="tertiary"
            onClick={() => {
              trackFeatureAbandoned(TRACKED_FEATURES.CHARACTER_UPLOAD_RIGGING, {
                character_id: characterID,
                step: AUTORIGGING_STEPS[step].shortTitle,
                step_index: step,
              });
              trackFeatureAbandoned(TRACKED_FEATURES.CHARACTER_UPLOAD, {
                character_id: characterID,
                step: 'rigging',
              });
              close();
            }}
          >
            Cancel
          </Button>
          <div className="flex gap-2">
            {step > 0 && (
              <Button
                variant="secondary"
                disabled={payload.autoRiggingInProgress}
                onClick={() => {
                  if (step === 1) {
                    setStep(0);
                  } else if (step === 2) {
                    // Do not call setStep(1), being on step two
                    // breaks the ability to go back to step one using React state.
                    // To bypass this problem we can instead update the modal payload to have showStep == 1
                    navigationStore.setModal(StudioModalName.AUTO_RIGGING, {
                      characterID,
                      showStep: 1,
                      autoRiggingInProgress: false,
                    });
                  }
                }}
              >
                {AUTORIGGING_STEPS[step].previousButtonText}
              </Button>
            )}
            <Button
              variant="primary"
              disabled={payload.autoRiggingInProgress}
              onClick={async () => {
                if (step === 0) {
                  trackFeatureStepCompleted(
                    TRACKED_FEATURES.CHARACTER_UPLOAD_RIGGING,
                    'orientation',
                    { character_id: characterID, step_index: 0 },
                  );
                  setStep(1);
                } else if (step === 1) {
                  if (
                    bodyMarkers.some((item) =>
                      item.markers.some((marker) => !marker.worldPosition),
                    )
                  ) {
                    const missingJoints = bodyMarkers
                      .filter((item) =>
                        item.markers.some((marker) => !marker.worldPosition),
                      )
                      .map((item) => item.name);
                    noticesStore.error(
                      `Could not find a joint for ${missingJoints.join(', ')}`,
                    );
                    return;
                  }

                  if (!meshData) return;

                  trackFeatureStepCompleted(
                    TRACKED_FEATURES.CHARACTER_UPLOAD_RIGGING,
                    'markers',
                    { character_id: characterID, step_index: 1 },
                  );

                  navigationStore.setModal(StudioModalName.AUTO_RIGGING, {
                    characterID,
                    showStep: 2,
                    autoRiggingInProgress: true,
                  });

                  const markers = getApiPayload(bodyMarkers);

                  await characterPickerStore.autoRigCharacter(
                    characterID,
                    markers,
                  );
                } else if (step === 2) {
                  trackFeatureCompleted(
                    TRACKED_FEATURES.CHARACTER_UPLOAD_RIGGING,
                    { character_id: characterID },
                  );
                  trackFeatureCompleted(TRACKED_FEATURES.CHARACTER_UPLOAD, {
                    character_id: characterID,
                    metadata: { resolution_path: 'auto-rigging' },
                  });
                  sceneSettingsStore.updateCharacter(characterID);
                  close();
                }
              }}
            >
              {AUTORIGGING_STEPS[step].nextButtonText}
            </Button>
          </div>
        </ModalLayout.Buttons>
      </ModalLayout.Root>
    </Modal>
  );
}
